'use client';

import React from 'react';
import clsx from 'clsx';
import { format } from 'url';
import { usePathname, useRouter } from 'next/navigation';
import type { LinkProps as NextLinkProps } from 'next/link';
import NextLink from 'next/link';
import { useIsBot } from '@packages/tracking/src/hooks/useIsBot/useIsBot';
import type { Language } from '@packages/config/src/default/types';
import { localizeUrl } from '@packages/shared/src/utils/localizeUrl/localizeUrl';
import { useI18n } from '@packages/shared/src/hooks/useI18n/useI18n';
import { usePageTransition } from '@packages/shared/src/providers/PageTransitionProvider/usePageTransition';
import { getCanonicalUrl } from '@packages/shared/src/utils/getCanonicalUrl/getCanonicalUrl';
import { useCookies } from '@packages/shared/src/providers/CookieProvider/CookieProvider';
import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import { useLocalizedPageUrl } from '@packages/shared/src/providers/LocalizedPageUrlProvider/useLocalizedPageUrl';
import { useClearedHref } from './useClearedHref/useClearedHref';

export interface NextLinkComposedProps
  extends Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'href'>,
    Omit<NextLinkProps, 'href' | 'as' | 'onMouseEnter' | 'onClick' | 'onTouchStart'> {
  to: NextLinkProps['href'];
  linkAs?: NextLinkProps['as'];
  href?: NextLinkProps['href'];
}

const NextLinkComposed = React.forwardRef<HTMLAnchorElement, Omit<NextLinkComposedProps, 'href'>>(
  (props, ref) => {
    const {
      children,
      to,
      linkAs,
      replace,
      scroll,
      shallow,
      prefetch = false,
      locale,
      onClick,
      onKeyDown,
      ...other
    } = props;
    const pageTransition = usePageTransition();
    const { setUrl } = useLocalizedPageUrl();
    // when pages router is not needed any more, uncomment this part
    // if (!pageTransition) {
    //   throw new Error('usePageTransition() must be used within a PageTransitionProvider');
    // }
    const router = useRouter();

    return (
      <NextLink
        href={to}
        prefetch={prefetch}
        as={linkAs}
        replace={replace}
        scroll={scroll}
        shallow={shallow}
        passHref
        legacyBehavior
      >
        <a
          ref={ref}
          onClick={
            // prevent default only when no page transition provider detected (actually only in app folder)
            pageTransition
              ? (e) => {
                  // give the consuming components a chance to prevent the default behavior and do something else, like in the navigation where clicks don't always directly change pages
                  onClick?.(e);
                  if (e.defaultPrevented) {
                    return;
                  }

                  e.preventDefault();
                  setUrl(to.toString());
                  pageTransition.start(() => {
                    router.push(to.toString());
                  });
                }
              : onClick
          }
          onKeyDown={(event) => {
            if (event.key === 'Enter' && pageTransition) {
              onKeyDown?.(event);
              if (event.defaultPrevented) {
                return;
              }

              event.preventDefault();
              setUrl(to.toString());
              pageTransition.start(() => {
                router.push(to.toString());
              });
            } else {
              onKeyDown?.(event);
            }
          }}
          {...other}
          role="link"
          tabIndex={0}
        >
          {children}
        </a>
      </NextLink>
    );
  },
);

NextLinkComposed.displayName = 'NextLinkComposed';

export type LinkProps = {
  activeClassName?: string;
  linkAs?: NextLinkProps['as']; // Useful when the as prop is shallow by styled().
  noLinkStyle?: boolean;
  openInNewTab?: boolean;
} & Omit<NextLinkComposedProps, 'to' | 'linkAs' | 'href'> &
  Pick<NextLinkProps, 'href' | 'as'>;

/**
 * A styled version  of the Next.js Link component: https://nextjs.org/docs/api-reference/next/link
 *
 * Automatically adds the current language to all `href`s, unless the `locale` prop is set explicitly.
 * */
const Link = React.forwardRef<HTMLAnchorElement, LinkProps>((props, ref) => {
  const {
    activeClassName = 'active',
    as: linkAs,
    className: classNameProps,
    href = '',
    noLinkStyle,
    openInNewTab,
    role, // Link don't have roles.
    locale,
    children,
    prefetch = false,
    ...other
  } = props;

  // NOTE: this currently strips out query params and hash for object urls, but keeps them for string urls
  const { linkHref, external } = useClearedHref(href);
  let linkTarget = href;
  const isBot = useIsBot();
  if (isBot) {
    linkTarget = getCanonicalUrl(typeof href === 'string' ? href : format(href));
  }

  // if we are in an appview, we can not render the next link. we need to use an default anchor so the redirect is not done via javascript
  const { getCookies } = useCookies();
  const { appView } = getCookies();
  const isApp = appView === 'true';

  const pathname = usePathname();

  const className = clsx(classNameProps, {
    // startsWith instead of equality to avoid false negatives with query params, and work around trailing slash inconsistencies between server and client
    // NOTE: null check is for pages/app router compat mode, later for pure app router this can never be null
    [activeClassName]: pathname && linkHref.startsWith(pathname) && activeClassName,
  });

  const target = openInNewTab ? '_blank' : '_self';

  const config = useConfig();
  const { language } = useI18n();

  if (external || noLinkStyle) {
    return (
      <a className={className} href={linkHref} ref={ref} target={target} {...other}>
        {children}
      </a>
    );
  }

  if (isApp) {
    const stringifiedAppHref = typeof linkTarget === 'string' ? linkTarget : format(linkTarget);

    return (
      <a className={className} href={stringifiedAppHref} ref={ref} target={target} {...other}>
        {children}
      </a>
    );
  }

  const stringifiedHref = typeof linkTarget === 'string' ? linkTarget : format(linkTarget);

  const targetLanguage = (locale ?? language) as Language;

  // always add the correct language prefix to the href, as the app router does not magically do this for us anymore like the pages router did
  // in cases where the link is intentionally for a different language, the `locale` prop has to be used for now
  const localizedHref =
    // ignore some cases of hrefs for backwards compatibility
    stringifiedHref.startsWith('/')
      ? localizeUrl(stringifiedHref, targetLanguage, config)
      : stringifiedHref;

  if (noLinkStyle) {
    return (
      <NextLinkComposed
        className={className}
        ref={ref}
        to={localizedHref}
        target={target}
        {...other}
      />
    );
  }

  return (
    <NextLink
      data-testid="link"
      as={linkAs}
      className={className}
      ref={ref}
      href={localizedHref}
      target={target}
      prefetch={prefetch}
      {...other}
    >
      {children}
    </NextLink>
  );
});

Link.displayName = 'Link';

export { NextLinkComposed, Link };
